import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { useSeasonYear, useSeasonYearContext } from '../context/SeasonYearContext';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Switch,
  FormControlLabel,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Checkbox,
  Alert,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { API_URL } from '../config';
import { fetchApi, authedFetch } from '../utils/apiUtils';
import debounce from 'lodash.debounce';
import { useNotification } from '../context/NotificationContext';
import { ConfirmDialog } from './common/ConfirmDialog';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MergeIcon from '@mui/icons-material/MergeType';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GroupsIcon from '@mui/icons-material/Groups';
import VerifiedIcon from '@mui/icons-material/Verified';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

interface Driver {
  driver_id: number;
  first_name: string;
  last_name: string;
  hometown: string;
  state: string;
  country: string;
  car_number: string;
  is_current_member: boolean;
}

interface DriverAlias {
  alias_id: number;
  first_name: string;
  last_name: string;
}

interface DriverFormData {
  first_name: string;
  last_name: string;
  hometown: string;
  state: string;
  country: string;
  car_number: string;
  is_current_member: boolean;
}

interface DriverStats {
  total: number;
  activeMembers: number;
  inactiveMembers: number;
}

interface SyncStats {
  status?: string;
  note?: string;
  foundMembers?: number;
  matched?: number;
  raceNameMatched?: number;
  created?: number;
  added?: number;
  hometownUpdated?: number;
  unmatched?: number;
  errors?: string[];
}

interface SyncResults {
  success: boolean;
  message?: string;
  error?: string;
  details?: string;
  stats?: SyncStats;
}

interface MergeConflicts {
  raceResults?: number;
  memberships?: number;
  standings?: number;
}

interface MergeResults {
  message: string;
  transferredResults: number;
  transferredMemberships: number;
  transferredStandings: number;
  conflicts?: MergeConflicts;
}

interface DriversSearchResponse {
  drivers: Driver[];
  total: number;
  activeMembers?: number;
  inactiveMembers?: number;
}

interface SyncPreviewPlan {
  foundExternalMembers: number;
  matched: number;
  matched_via_race_name?: number;
  duplicates_in_external: number;
  skipped_missing_name: number;
  would_create_drivers: number;
  would_add_memberships_existing: number;
  would_update_location: number;
}

interface SyncPreview {
  year: number;
  plan: SyncPreviewPlan;
  samples: {
    create_drivers: { name: string; hometown: string; state: string }[];
    add_memberships: { driver_id: number; name: string }[];
    race_name_matches: { member_name: string; race_name: string; driver_id: number; driver_name: string }[];
    update_location: { driver_id: number; name: string; new_hometown: string; new_state: string; existing_membership: boolean }[];
  };
  sample_size: number;
  errors: string[];
  errors_total: number;
}

function DriverManagement(): React.ReactElement {
  const configuredSeasonYear = useSeasonYear();
  const { setSeasonYear: setGlobalSeasonYear } = useSeasonYearContext();
  const [seasonAnchorEl, setSeasonAnchorEl] = useState<null | HTMLElement>(null);
  const [yearOptions, setYearOptions] = useState<number[]>([configuredSeasonYear]);
  const { showSuccess, showError } = useNotification();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<DriverFormData>({
    first_name: '',
    last_name: '',
    hometown: '',
    state: '',
    country: 'USA',
    car_number: '',
    is_current_member: false
  });
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [total, setTotal] = useState<number>(0);
  const [driverStats, setDriverStats] = useState<DriverStats>({ total: 0, activeMembers: 0, inactiveMembers: 0 });
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [syncResults, setSyncResults] = useState<SyncResults | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState<boolean>(false);
  const [showSyncPreview, setShowSyncPreview] = useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<SyncPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  
  // Merge functionality state
  const [showMergeDialog, setShowMergeDialog] = useState<boolean>(false);
  const [mergeSourceDriver, setMergeSourceDriver] = useState<Driver | null>(null);
  const [mergeTargetDriver, setMergeTargetDriver] = useState<Driver | null>(null);
  const [mergeReason, setMergeReason] = useState<string>('');
  const [mergeLoading, setMergeLoading] = useState<boolean>(false);
  const [mergeResults, setMergeResults] = useState<MergeResults | null>(null);
  const [showMergeResultsDialog, setShowMergeResultsDialog] = useState<boolean>(false);
  const [mergeTargetSearchText, setMergeTargetSearchText] = useState<string>('');
  const [togglingDriverIds, setTogglingDriverIds] = useState<Set<number>>(new Set());
  const [mergeTargetSearchResults, setMergeTargetSearchResults] = useState<Driver[]>([]);
  const [mergeTargetSearchLoading, setMergeTargetSearchLoading] = useState<boolean>(false);
  const [showMergeConfirmation, setShowMergeConfirmation] = useState<boolean>(false);
  const [mergeConfirmed, setMergeConfirmed] = useState<boolean>(false);

  const [driverAliases, setDriverAliases] = useState<DriverAlias[]>([]);
  const [aliasFirstName, setAliasFirstName] = useState<string>('');
  const [aliasLastName, setAliasLastName] = useState<string>('');
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [driverToDelete, setDriverToDelete] = useState<Driver | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Driver',
      flex: 1,
      minWidth: 180,
      valueGetter: (params: GridValueGetterParams) => {
        if (!params || !params.row) return '';
        return `${params.row.first_name ?? ''} ${params.row.last_name ?? ''}`;
      },
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ fontWeight: 500 }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'car_number',
      headerName: 'Car #',
      width: 80,
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 200,
      valueGetter: (params: GridValueGetterParams) => {
        if (!params || !params.row) return '';
        return `${params.row.hometown ?? ''}${params.row.state ? `, ${params.row.state}` : ''}`;
      }
    },
    {
      field: 'is_current_member',
      headerName: 'Member',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Non-Member'}
          color={params.value ? 'success' : 'default'}
          variant="outlined"
          size="small"
          onClick={() => handleMembershipToggle(params.row.driver_id, params.value as boolean)}
          disabled={togglingDriverIds.has(params.row.driver_id)}
          sx={{ cursor: 'pointer', fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Edit driver">
            <IconButton
              size="small"
              onClick={() => handleEditDriver(params.row as Driver)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'primary.main', backgroundColor: 'action.hover' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Merge driver">
            <IconButton
              size="small"
              onClick={() => handleMergeDriver(params.row as Driver)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'warning.main', backgroundColor: 'action.hover' },
              }}
            >
              <MergeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete driver">
            <IconButton
              size="small"
              onClick={() => handleDeleteDriver(params.row as Driver)}
              sx={{
                color: 'text.secondary',
                '&:hover, &.Mui-focusVisible': { color: 'error.main', backgroundColor: 'action.hover' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const fetchDrivers = async (
    query: string = searchQuery, 
    pageArg: number = page, 
    pageSizeArg: number = pageSize, 
    filter: string = filterStatus
  ): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        query,
        limit: String(pageSizeArg),
        offset: String(pageArg * pageSizeArg),
        status: filter !== 'all' ? (filter === 'active' ? 'true' : 'false') : 'all',
        year: String(configuredSeasonYear)
      });
      const data = await fetchApi(`${API_URL}/drivers/search?${params.toString()}`) as DriversSearchResponse;
      
      setDrivers(data.drivers);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      showError('Failed to load drivers. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const debouncedSearch = useMemo(
    () => debounce((query: string) => {
      fetchDrivers(query, 0, pageSize, filterStatus);
    }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageSize, filterStatus, configuredSeasonYear]
  );

  useEffect(() => {
    if (searchQuery.length > 0) {
      debouncedSearch(searchQuery);
    } else {
      fetchDrivers(searchQuery, page, pageSize, filterStatus);
    }
    // eslint-disable-next-line
  }, [searchQuery, filterStatus, page, pageSize, configuredSeasonYear]);

  useEffect(() => {
    authedFetch(`${API_URL}/config/available-seasons`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data: { years: number[] }) => {
        if (data.years?.length > 0) setYearOptions(data.years);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchStats = async (): Promise<void> => {
      try {
        const data = await fetchApi(`${API_URL}/drivers/stats?year=${configuredSeasonYear}`) as DriverStats;
        setDriverStats({
          total: data.total || 0,
          activeMembers: data.activeMembers || 0,
          inactiveMembers: data.inactiveMembers || 0
        });
      } catch {
        setDriverStats({ total: 0, activeMembers: 0, inactiveMembers: 0 });
      }
    };
    fetchStats();
  }, [configuredSeasonYear]);

  const handleSearch = useCallback((query: string): void => {
    setSearchQuery(query);
    setPage(0);
  }, []);

  const handleFilterStatusChange = (newStatus: string): void => {
    setFilterStatus(newStatus);
    setPage(0);
  };

  const handleMembershipToggle = async (driverId: number, currentStatus: boolean): Promise<void> => {
    if (togglingDriverIds.has(driverId)) return;
    setTogglingDriverIds(prev => new Set(prev).add(driverId));

    const updatedDrivers = drivers.map(driver =>
      driver.driver_id === driverId
        ? { ...driver, is_current_member: !currentStatus }
        : driver
    );
    setDrivers(updatedDrivers);

    try {
      if (currentStatus) {
        await fetchApi(`${API_URL}/drivers/${driverId}/memberships/${configuredSeasonYear}`, {
          method: 'DELETE'
        });
        showSuccess('Membership removed successfully');
      } else {
        await fetchApi(`${API_URL}/drivers/${driverId}/memberships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year: configuredSeasonYear }),
        });
        showSuccess('Membership activated successfully');
      }
      fetchDrivers();
    } catch (err) {
      console.error('Error toggling membership:', err);
      showError('Failed to update membership status. Please try again.');
      fetchDrivers();
    } finally {
      setTogglingDriverIds(prev => {
        const next = new Set(prev);
        next.delete(driverId);
        return next;
      });
    }
  };

  const handleEditDriver = (driver: Driver): void => {
    setError(null);
    setSelectedDriver(driver);
    setFormData({
      first_name: driver.first_name,
      last_name: driver.last_name,
      hometown: driver.hometown,
      state: driver.state,
      country: driver.country,
      car_number: driver.car_number,
      is_current_member: driver.is_current_member
    });
    setDriverAliases([]);
    setAliasFirstName('');
    setAliasLastName('');
    fetchApi(`${API_URL}/drivers/${driver.driver_id}/aliases`)
      .then((rows) => setDriverAliases(rows as DriverAlias[]))
      .catch((err) => console.error('Error fetching aliases:', err));
    setOpenDialog(true);
  };

  const handleAddAlias = async (): Promise<void> => {
    if (!selectedDriver || !aliasFirstName.trim() || !aliasLastName.trim()) return;
    try {
      const alias = await fetchApi(`${API_URL}/drivers/${selectedDriver.driver_id}/aliases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: aliasFirstName.trim(), last_name: aliasLastName.trim() }),
      }) as DriverAlias;
      setDriverAliases((prev) => [...prev, alias]);
      setAliasFirstName('');
      setAliasLastName('');
    } catch (err) {
      // fetchApi only surfaces `message` from error bodies, so the 409's
      // `error` field arrives as "HTTP error! status: 409".
      const message = err instanceof Error ? err.message : 'Failed to add alias';
      showError(message.includes('409') ? 'That name is already an alias of another driver' : message);
    }
  };

  const handleDeleteAlias = async (aliasId: number): Promise<void> => {
    if (!selectedDriver) return;
    try {
      await fetchApi(`${API_URL}/drivers/${selectedDriver.driver_id}/aliases/${aliasId}`, { method: 'DELETE' });
      setDriverAliases((prev) => prev.filter((a) => a.alias_id !== aliasId));
    } catch (err) {
      console.error('Error deleting alias:', err);
      showError('Failed to remove alias');
    }
  };

  const handleDeleteDriver = (driver: Driver): void => {
    setDriverToDelete(driver);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteDriver = async (): Promise<void> => {
    if (!driverToDelete) return;
    
    setDeleteLoading(true);
    try {
      await fetchApi(`${API_URL}/drivers/${driverToDelete.driver_id}`, { method: 'DELETE' });
      showSuccess(`Driver ${driverToDelete.first_name} ${driverToDelete.last_name} deleted successfully`);
      fetchDrivers();
      setDeleteDialogOpen(false);
      setDriverToDelete(null);
    } catch (err) {
      console.error('Error deleting driver:', err);
      showError('Failed to delete driver. This driver may have associated race results.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleMergeDriver = (driver: Driver): void => {
    setError(null);
    setMergeSourceDriver(driver);
    setMergeTargetDriver(null);
    setMergeReason('');
    setMergeTargetSearchText('');
    setMergeTargetSearchResults([]);
    setShowMergeConfirmation(false);
    setMergeConfirmed(false);
    setShowMergeDialog(true);
  };

  const searchMergeTargetDrivers = async (searchText: string): Promise<void> => {
    if (searchText.length < 2) {
      setMergeTargetSearchResults([]);
      return;
    }

    setMergeTargetSearchLoading(true);
    try {
      const data = await fetchApi(`${API_URL}/drivers/search?query=${encodeURIComponent(searchText)}&limit=10`) as DriversSearchResponse;
      
      const filteredResults = data.drivers.filter(driver => 
        driver.driver_id !== mergeSourceDriver?.driver_id
      );
      
      setMergeTargetSearchResults(filteredResults);
    } catch (err) {
      console.error('Error searching drivers:', err);
      setMergeTargetSearchResults([]);
    } finally {
      setMergeTargetSearchLoading(false);
    }
  };

  const handleMergeTargetSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const searchText = e.target.value;
    setMergeTargetSearchText(searchText);
    
    if (mergeTargetDriver && !searchText.includes(mergeTargetDriver.driver_id.toString())) {
      setMergeTargetDriver(null);
    }
    
    clearTimeout((window as unknown as { mergeSearchTimeout: number }).mergeSearchTimeout);
    (window as unknown as { mergeSearchTimeout: number }).mergeSearchTimeout = window.setTimeout(() => {
      searchMergeTargetDrivers(searchText);
    }, 300);
  };

  const handleSelectMergeTargetDriver = (driver: Driver): void => {
    setMergeTargetDriver(driver);
    setMergeTargetSearchText(`${driver.first_name} ${driver.last_name} (ID: ${driver.driver_id})`);
    setMergeTargetSearchResults([]);
  };

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent): void => {
      if (showMergeDialog && !(event.target as HTMLElement).closest('.merge-search-container')) {
        setMergeTargetSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMergeDialog]);

  const handleMergeSubmit = async (): Promise<void> => {
    if (!mergeSourceDriver || !mergeTargetDriver) {
      setError('Please select both source and target drivers');
      return;
    }

    if (mergeSourceDriver.driver_id === mergeTargetDriver.driver_id) {
      setError('Source and target drivers cannot be the same');
      return;
    }

    if (!showMergeConfirmation) {
      setShowMergeConfirmation(true);
      setError(null);
      return;
    }

    if (!mergeConfirmed) {
      setError('Please confirm that you have verified all details before proceeding');
      return;
    }

    setMergeLoading(true);
    try {
      const data = await fetchApi(`${API_URL}/drivers/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceDriverId: mergeSourceDriver.driver_id,
          targetDriverId: mergeTargetDriver.driver_id,
          reason: mergeReason
        }),
      }) as MergeResults;

      setMergeResults(data);
      setShowMergeDialog(false);
      setShowMergeResultsDialog(true);
      fetchDrivers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error merging drivers:', err);
    } finally {
      setMergeLoading(false);
    }
  };

  const handleBackToEdit = (): void => {
    setShowMergeConfirmation(false);
    setMergeConfirmed(false);
    setError(null);
  };

  const handleExport = (): void => {
    const csvContent = [
      ['ID', 'First Name', 'Last Name', 'Car Number', 'Hometown', 'State', 'Country', 'Membership Status'],
      ...drivers.map(driver => [
        driver.driver_id,
        driver.first_name,
        driver.last_name,
        driver.car_number,
        driver.hometown,
        driver.state,
        driver.country,
        driver.is_current_member ? 'Active' : 'Inactive'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `drivers_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (): Promise<void> => {
    setError(null);

    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      return;
    }

    try {
      const method = selectedDriver ? 'PUT' : 'POST';
      const url = selectedDriver
        ? `${API_URL}/drivers/${selectedDriver.driver_id}`
        : `${API_URL}/drivers`;

      // Only send is_current_member when the toggle was actually changed —
      // sending a stale value on every save silently deleted memberships.
      const payload: Record<string, unknown> = { ...formData, year: configuredSeasonYear };
      if (selectedDriver && formData.is_current_member === selectedDriver.is_current_member) {
        delete payload.is_current_member;
      }

      const response = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save driver');
      }

      setOpenDialog(false);
      setSelectedDriver(null);
      setFormData({
        first_name: '',
        last_name: '',
        hometown: '',
        state: '',
        country: 'USA',
        car_number: '',
        is_current_member: false
      });
      fetchDrivers();
    } catch (err) {
      console.error('Error saving driver:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    }
  };

  const pollSyncStatus = () => {
    const maxAttempts = 120; // 10 minutes at 5s intervals
    let attempts = 0;

    const poll = async () => {
      attempts++;
      try {
        const status = await fetchApi(`${API_URL}/drivers/sync-status`) as {
          status: string;
          message: string;
          progress?: number;
          stats?: SyncStats;
          error?: string;
        };

        if (status.status === 'completed') {
          setSyncResults({
            success: true,
            message: status.message,
            stats: status.stats
          });
          setSyncLoading(false);
          fetchDrivers();
          try {
            const statsData = await fetchApi(`${API_URL}/drivers/stats?year=${configuredSeasonYear}`) as DriverStats;
            setDriverStats({
              total: statsData.total || 0,
              activeMembers: statsData.activeMembers || 0,
              inactiveMembers: statsData.inactiveMembers || 0
            });
          } catch { /* best-effort stats refresh; sync result already shown */ }
          return;
        }

        if (status.status === 'error') {
          setSyncResults({
            success: false,
            error: status.message,
            details: status.error
          });
          setSyncLoading(false);
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setSyncResults({
            success: false,
            error: 'Sync status polling timed out. Check server logs for results.'
          });
          setSyncLoading(false);
        }
      } catch {
        setSyncResults({
          success: false,
          error: 'Failed to check sync status'
        });
        setSyncLoading(false);
      }
    };

    setTimeout(poll, 3000);
  };

  const loadSyncPreview = async (): Promise<void> => {
    setShowSyncPreview(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      const data = await fetchApi(`${API_URL}/drivers/sync-memberships?dry_run=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: configuredSeasonYear }),
      }) as SyncPreview;
      setPreviewData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load preview';
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSyncMemberships = async (): Promise<void> => {
    setShowSyncPreview(false);
    setSyncLoading(true);
    setSyncResults(null);

    try {
      await fetchApi(`${API_URL}/drivers/sync-memberships`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: configuredSeasonYear }),
      });

      setSyncResults({
        success: true,
        message: 'Membership sync has been started in the background.',
        stats: {
          status: 'started',
          note: 'Progress will update automatically.'
        }
      });
      setShowSyncDialog(true);
      pollSyncStatus();

    } catch (err) {
      console.error('Error syncing memberships:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setSyncResults({
        success: false,
        error: 'Failed to start membership sync',
        details: errorMessage
      });
      setShowSyncDialog(true);
      setSyncLoading(false);
    }
  };

  const stats = [
    {
      label: 'Total Drivers',
      value: driverStats.total.toLocaleString(),
      icon: <GroupsIcon />,
    },
    {
      label: 'Active Members',
      value: driverStats.activeMembers.toLocaleString(),
      icon: <VerifiedIcon />,
      tone: 'success' as const,
    },
    {
      label: 'Non-Members',
      value: driverStats.inactiveMembers.toLocaleString(),
      icon: <PersonOffIcon />,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Drivers
          </Typography>
          <Button
            onClick={(e) => setSeasonAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 9999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
            }}
          >
            <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75, fontWeight: 500 }}>
              Season
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {configuredSeasonYear}
            </Typography>
          </Button>
          <Menu
            anchorEl={seasonAnchorEl}
            open={Boolean(seasonAnchorEl)}
            onClose={() => setSeasonAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 100 } } }}
          >
            {yearOptions.map((y) => (
              <MenuItem
                key={y}
                selected={y === configuredSeasonYear}
                onClick={() => {
                  setGlobalSeasonYear(y);
                  setSeasonAnchorEl(null);
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={syncLoading ? <CircularProgress size={14} /> : <SyncIcon sx={{ fontSize: 16 }} />}
            onClick={loadSyncPreview}
            disabled={syncLoading}
          >
            {syncLoading ? 'Syncing' : 'Sync Memberships'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setError(null);
              setSelectedDriver(null);
              setFormData({
                first_name: '',
                last_name: '',
                hometown: '',
                state: '',
                country: 'USA',
                car_number: '',
                is_current_member: false,
              });
              setOpenDialog(true);
            }}
          >
            Add Driver
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={12} sm={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box
                    sx={{
                      color: stat.tone === 'success' ? 'success.main' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.1,
                  }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            placeholder="Search drivers..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e: SelectChangeEvent) => handleFilterStatusChange(e.target.value)}
              label="Filter Status"
            >
              <MenuItem value="all">All Drivers</MenuItem>
              <MenuItem value="active">Active Members</MenuItem>
              <MenuItem value="inactive">Non-Members</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ height: 700, width: '100%' }}>
        <DataGrid
          rows={drivers}
          columns={columns}
          getRowId={(row) => row.driver_id}
          loading={loading}
          rowCount={total}
          pagination
          page={page}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          paginationMode="server"
          onPageChange={(newPage) => {
            setPage(newPage);
          }}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          disableSelectionOnClick
          sx={{
            border: 'none',
            fontFamily: 'inherit',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
              borderBottom: '1px solid',
              borderColor: 'divider',
              minHeight: '40px !important',
              maxHeight: '40px !important',
              lineHeight: '40px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '0 12px',
              '&:focus, &:focus-within': { outline: 'none' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
            },
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
            '& .MuiDataGrid-cell': {
              padding: '0 12px',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderBottom: '1px solid',
              borderColor: 'grey.100',
              '&:focus, &:focus-within': { outline: 'none' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
            },
            '& .MuiDataGrid-row': {
              transition: 'background-color 120ms',
              '&:hover': { backgroundColor: 'action.hover' },
            },
            '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              minHeight: '44px',
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.8125rem',
            },
          }}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedDriver ? 'Edit Driver' : 'Add Driver'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Car Number"
                value={formData.car_number}
                onChange={(e) => setFormData({ ...formData, car_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Hometown"
                value={formData.hometown}
                onChange={(e) => setFormData({ ...formData, hometown: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.state}
                  label="State"
                  onChange={(e: SelectChangeEvent) => setFormData({ ...formData, state: e.target.value })}
                >
                  {US_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_current_member}
                    onChange={(e) => setFormData({ ...formData, is_current_member: e.target.checked })}
                  />
                }
                label="Current member"
              />
            </Grid>
            {selectedDriver && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Also registered as
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  The membership sync matches these names to this driver, so a member who
                  races under a different name stays linked.
                </Typography>
                {driverAliases.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {driverAliases.map((alias) => (
                      <Chip
                        key={alias.alias_id}
                        size="small"
                        label={`${alias.first_name} ${alias.last_name}`}
                        onDelete={() => handleDeleteAlias(alias.alias_id)}
                      />
                    ))}
                  </Stack>
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label="First Name"
                    value={aliasFirstName}
                    onChange={(e) => setAliasFirstName(e.target.value)}
                  />
                  <TextField
                    size="small"
                    label="Last Name"
                    value={aliasLastName}
                    onChange={(e) => setAliasLastName(e.target.value)}
                  />
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddAlias}
                    disabled={!aliasFirstName.trim() || !aliasLastName.trim()}
                  >
                    Add
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedDriver ? 'Update' : 'Add'} Driver
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSyncPreview}
        onClose={() => setShowSyncPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Sync preview · {configuredSeasonYear}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            Review the planned changes before running the actual sync. Nothing has been written yet.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {previewLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Building preview from membership API...
              </Typography>
            </Box>
          )}

          {!previewLoading && previewError && (
            <Alert severity="error">{previewError}</Alert>
          )}

          {!previewLoading && previewData && (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  { label: 'Drivers to Create', value: previewData.plan.would_create_drivers, tone: 'info' as const },
                  { label: 'Memberships to Add', value: previewData.plan.would_add_memberships_existing + previewData.plan.would_create_drivers, tone: 'success' as const },
                  { label: 'Location Updates', value: previewData.plan.would_update_location, tone: 'default' as const },
                ].map((stat) => (
                  <Grid key={stat.label} item xs={6} sm={4}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            color:
                              stat.tone === 'warning' ? 'warning.main' :
                              stat.tone === 'success' ? 'success.main' :
                              stat.tone === 'info' ? 'info.main' : 'text.primary',
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Source · {previewData.plan.foundExternalMembers} active members in {previewData.year} · {previewData.plan.matched} matched locally
                {(previewData.plan.matched_via_race_name ?? 0) > 0 && ` (${previewData.plan.matched_via_race_name} via race name)`}
                {previewData.plan.duplicates_in_external > 0 && ` · ${previewData.plan.duplicates_in_external} duplicate names`}
              </Typography>

              {(['create_drivers', 'add_memberships', 'race_name_matches', 'update_location'] as const).map((bucket) => {
                const items = previewData.samples[bucket];
                if (!items || items.length === 0) return null;
                const titles = {
                  create_drivers: 'Sample drivers to create',
                  add_memberships: 'Sample memberships to add',
                  race_name_matches: 'Sample race-name matches (verify these)',
                  update_location: 'Sample location updates',
                };
                return (
                  <Box key={bucket} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>
                      {titles[bucket]} (first {items.length})
                    </Typography>
                    <Paper variant="outlined" sx={{ maxHeight: 180, overflow: 'auto' }}>
                      <List dense disablePadding>
                        {items.map((item, idx) => {
                          if (bucket === 'create_drivers') {
                            const it = item as SyncPreview['samples']['create_drivers'][number];
                            return (
                              <ListItem key={idx} divider={idx < items.length - 1}>
                                <ListItemText
                                  primary={it.name}
                                  secondary={`${it.hometown || '-'}${it.state ? `, ${it.state}` : ''}`}
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            );
                          }
                          if (bucket === 'race_name_matches') {
                            const it = item as SyncPreview['samples']['race_name_matches'][number];
                            return (
                              <ListItem key={idx} divider={idx < items.length - 1}>
                                <ListItemText
                                  primary={`${it.member_name} -> ${it.driver_name}`}
                                  secondary={`Race name "${it.race_name}" · Driver ID ${it.driver_id}`}
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            );
                          }
                          if (bucket === 'update_location') {
                            const it = item as SyncPreview['samples']['update_location'][number];
                            return (
                              <ListItem key={idx} divider={idx < items.length - 1}>
                                <ListItemText
                                  primary={it.name}
                                  secondary={`${it.new_hometown}, ${it.new_state}${it.existing_membership ? ' · existing member' : ''}`}
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            );
                          }
                          const it = item as SyncPreview['samples']['add_memberships'][number];
                          return (
                            <ListItem key={idx} divider={idx < items.length - 1}>
                              <ListItemText
                                primary={it.name}
                                secondary={`Driver ID ${it.driver_id}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  </Box>
                );
              })}

              {previewData.errors_total > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Warnings ({previewData.errors_total})
                  </Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 140, overflow: 'auto', p: 1.5 }}>
                    {previewData.errors.map((err, i) => (
                      <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {err}
                      </Typography>
                    ))}
                    {previewData.errors_total > previewData.errors.length && (
                      <Typography variant="caption" color="text.disabled">
                        ...and {previewData.errors_total - previewData.errors.length} more
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSyncPreview(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSyncMemberships}
            variant="contained"
            disabled={previewLoading || !previewData}
          >
            Run sync {configuredSeasonYear}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Membership Sync</DialogTitle>
        <DialogContent>
          {syncResults?.success && syncResults.stats?.status === 'started' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {syncResults.message} {syncResults.stats.note}
            </Alert>
          )}
          {syncResults?.success && syncResults.stats?.status !== 'started' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Sync completed successfully.
              </Alert>
              <Grid container spacing={1.5}>
                {[
                  { label: 'Members Found', value: syncResults.stats?.foundMembers ?? 0 },
                  { label: 'Matched', value: syncResults.stats?.matched ?? 0, tone: 'success' as const },
                  { label: 'Via Race Name', value: syncResults.stats?.raceNameMatched ?? 0 },
                  { label: 'Created', value: syncResults.stats?.created ?? 0 },
                  { label: 'Memberships Added', value: syncResults.stats?.added ?? 0 },
                  { label: 'Location Updated', value: syncResults.stats?.hometownUpdated ?? 0 },
                  { label: 'Unmatched', value: syncResults.stats?.unmatched ?? 0, tone: 'error' as const },
                ].map((stat) => (
                  <Grid key={stat.label} item xs={6} sm={4} md={3}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            color:
                              stat.tone === 'success'
                                ? 'success.main'
                                : stat.tone === 'warning'
                                ? 'warning.main'
                                : stat.tone === 'error'
                                ? 'error.main'
                                : 'text.primary',
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {syncResults.stats?.errors && syncResults.stats.errors.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em' }}>
                    Issues
                  </Typography>
                  <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflow: 'auto', p: 1.5 }}>
                    {syncResults.stats.errors.slice(0, 10).map((err, index) => (
                      <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {err}
                      </Typography>
                    ))}
                    {syncResults.stats.errors.length > 10 && (
                      <Typography variant="caption" color="text.disabled">
                        ...and {syncResults.stats.errors.length - 10} more
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}
            </>
          )}
          {syncResults && !syncResults.success && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {syncResults.error}
              </Alert>
              {syncResults.details && (
                <Typography variant="body2" color="text.secondary">
                  {syncResults.details}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSyncDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showMergeDialog}
        onClose={() => setShowMergeDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {showMergeConfirmation ? 'Confirm merge' : 'Merge duplicate drivers'}
        </DialogTitle>
        <DialogContent>
          {!showMergeConfirmation ? (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Race results, memberships, and standings will be transferred from source to target. The source driver will be permanently deleted.
              </Alert>

              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                Source (will be deleted)
              </Typography>
              {mergeSourceDriver && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {mergeSourceDriver.first_name} {mergeSourceDriver.last_name}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ID {mergeSourceDriver.driver_id}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Car #{mergeSourceDriver.car_number || '-'} · {mergeSourceDriver.hometown || '-'}{mergeSourceDriver.state ? `, ${mergeSourceDriver.state}` : ''}
                  </Typography>
                </Paper>
              )}

              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                Target (will keep all data)
              </Typography>
              <Box sx={{ position: 'relative', mb: 2 }} className="merge-search-container">
                <TextField
                  fullWidth
                  size="small"
                  label="Search target driver"
                  placeholder="Name or car number"
                  value={mergeTargetSearchText}
                  onChange={handleMergeTargetSearchChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {mergeTargetSearchText && (
                            <IconButton
                              size="small"
                              aria-label="Clear target driver search"
                              onClick={() => {
                                setMergeTargetSearchText('');
                                setMergeTargetDriver(null);
                                setMergeTargetSearchResults([]);
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          {mergeTargetSearchLoading ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          )}
                        </Stack>
                      </InputAdornment>
                    ),
                  }}
                />
                {(mergeTargetSearchResults.length > 0 ||
                  (mergeTargetSearchText.length > 2 && !mergeTargetSearchLoading && mergeTargetSearchResults.length === 0)) && (
                  <Paper
                    variant="outlined"
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 0.5,
                      zIndex: 1400,
                      maxHeight: 220,
                      overflow: 'auto',
                    }}
                  >
                    <List dense disablePadding>
                      {mergeTargetSearchResults.length > 0 ? (
                        mergeTargetSearchResults.map((driver) => (
                          <ListItemButton
                            key={driver.driver_id}
                            onClick={() => handleSelectMergeTargetDriver(driver)}
                          >
                            <ListItemText
                              primary={`${driver.first_name} ${driver.last_name}`}
                              secondary={`ID ${driver.driver_id} · Car #${driver.car_number || 'N/A'} · ${driver.hometown || '-'}${driver.state ? `, ${driver.state}` : ''}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItemButton>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText
                            primary="No drivers found"
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', textAlign: 'center' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
              </Box>

              {mergeTargetDriver && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 3, borderColor: 'success.light' }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {mergeTargetDriver.first_name} {mergeTargetDriver.last_name}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ID {mergeTargetDriver.driver_id}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Car #{mergeTargetDriver.car_number || '-'} · {mergeTargetDriver.hometown || '-'}{mergeTargetDriver.state ? `, ${mergeTargetDriver.state}` : ''}
                  </Typography>
                </Paper>
              )}

              <TextField
                fullWidth
                size="small"
                label="Reason for merge"
                placeholder="e.g., duplicate entry, same driver with different car numbers"
                value={mergeReason}
                onChange={(e) => setMergeReason(e.target.value)}
                multiline
                rows={3}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Final verification required. Review the details below. This cannot be undone.
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="overline"
                      color="error.main"
                      sx={{ letterSpacing: '0.04em', display: 'block', mb: 0.5 }}
                    >
                      Source · will be deleted
                    </Typography>
                    {mergeSourceDriver && (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {mergeSourceDriver.first_name} {mergeSourceDriver.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          ID {mergeSourceDriver.driver_id} · Car #{mergeSourceDriver.car_number || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {mergeSourceDriver.hometown || '-'}{mergeSourceDriver.state ? `, ${mergeSourceDriver.state}` : ''}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography
                      variant="overline"
                      color="success.main"
                      sx={{ letterSpacing: '0.04em', display: 'block', mb: 0.5 }}
                    >
                      Target · keeps all data
                    </Typography>
                    {mergeTargetDriver && (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {mergeTargetDriver.first_name} {mergeTargetDriver.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          ID {mergeTargetDriver.driver_id} · Car #{mergeTargetDriver.car_number || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {mergeTargetDriver.hometown || '-'}{mergeTargetDriver.state ? `, ${mergeTargetDriver.state}` : ''}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {mergeSourceDriver && mergeTargetDriver && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em' }}>
                    What happens
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    <Typography variant="body2">
                      Race results, memberships, and standings move from {mergeSourceDriver.first_name} {mergeSourceDriver.last_name} to {mergeTargetDriver.first_name} {mergeTargetDriver.last_name}.
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      {mergeSourceDriver.first_name} {mergeSourceDriver.last_name} (ID {mergeSourceDriver.driver_id}) is permanently deleted.
                    </Typography>
                  </Stack>
                </Box>
              )}

              {mergeReason && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em', display: 'block', mb: 0.5 }}>
                    Reason
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2">{mergeReason}</Typography>
                  </Paper>
                </Box>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={mergeConfirmed}
                    onChange={(e) => setMergeConfirmed(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Typography variant="body2">
                    I have reviewed the details and want to proceed. This cannot be undone.
                  </Typography>
                }
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMergeDialog(false)} color="inherit">
            Cancel
          </Button>
          {showMergeConfirmation && (
            <Button onClick={handleBackToEdit} color="inherit">
              Back
            </Button>
          )}
          <Button
            onClick={handleMergeSubmit}
            variant="contained"
            color={showMergeConfirmation ? 'error' : 'primary'}
            disabled={!mergeTargetDriver || mergeLoading || (showMergeConfirmation && !mergeConfirmed)}
          >
            {mergeLoading ? 'Merging' : showMergeConfirmation ? 'Execute merge' : 'Review and confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showMergeResultsDialog}
        onClose={() => setShowMergeResultsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Merge results</DialogTitle>
        <DialogContent>
          {mergeResults && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                {mergeResults.message}
              </Alert>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  { label: 'Race Results', value: mergeResults.transferredResults },
                  { label: 'Memberships', value: mergeResults.transferredMemberships },
                  { label: 'Standings', value: mergeResults.transferredStandings },
                  {
                    label: 'Conflicts Resolved',
                    value:
                      (mergeResults.conflicts?.raceResults || 0) +
                      (mergeResults.conflicts?.memberships || 0) +
                      (mergeResults.conflicts?.standings || 0),
                    tone: 'warning' as const,
                  },
                ].map((stat) => (
                  <Grid key={stat.label} item xs={6} sm={3}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            color: stat.tone === 'warning' ? 'warning.main' : 'text.primary',
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {((mergeResults.conflicts?.raceResults ?? 0) > 0 ||
                (mergeResults.conflicts?.memberships ?? 0) > 0 ||
                (mergeResults.conflicts?.standings ?? 0) > 0) && (
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em' }}>
                    Conflicts resolved
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {(mergeResults.conflicts?.raceResults ?? 0) > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {mergeResults.conflicts?.raceResults} duplicate race result(s) removed (kept target)
                      </Typography>
                    )}
                    {(mergeResults.conflicts?.memberships ?? 0) > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {mergeResults.conflicts?.memberships} duplicate membership(s) removed (kept target)
                      </Typography>
                    )}
                    {(mergeResults.conflicts?.standings ?? 0) > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {mergeResults.conflicts?.standings} duplicate standing(s) removed (kept target)
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMergeResultsDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Driver"
        message={driverToDelete ? `Are you sure you want to delete ${driverToDelete.first_name} ${driverToDelete.last_name}? This action cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        onConfirm={confirmDeleteDriver}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDriverToDelete(null);
        }}
        loading={deleteLoading}
      />
    </Box>
  );
}

export default DriverManagement;
